"use client";

interface RangeSliderProps {
  value: number;
  onChange: (val: number) => void;
  min: number;
  max: number;
  step?: number;
  label?: string;
  suffix?: string;
}

export default function RangeSlider({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  suffix = "",
}: RangeSliderProps) {
  // Calculate percentage for progress gradient
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="slider-wrapper">
      <div className="slider-header">
        {label && <span className="slider-label">{label}</span>}
        <span className="slider-value">
          {value}
          {suffix}
        </span>
      </div>

      <div className="slider-container">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="custom-slider"
          style={{
            background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${percentage}%, var(--border-color) ${percentage}%, var(--border-color) 100%)`,
          }}
        />
      </div>

      <div className="slider-footer">
        <span className="slider-limit">{min}{suffix}</span>
        <span className="slider-limit">{max}{suffix}</span>
      </div>

      <style jsx>{`
        .slider-wrapper {
          width: 100%;
          margin-bottom: var(--space-4);
        }
        .slider-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-2);
        }
        .slider-label {
          font-family: var(--font-heading);
          font-size: var(--text-sm);
          font-weight: var(--fw-bold);
          color: var(--text-secondary);
        }
        .slider-value {
          font-family: var(--font-body);
          font-size: var(--text-sm);
          font-weight: var(--fw-extrabold);
          color: var(--primary);
          background-color: var(--primary-light);
          padding: 0.125rem 0.5rem;
          border-radius: var(--radius-sm);
        }
        .slider-container {
          position: relative;
          display: flex;
          align-items: center;
          height: 1.5rem;
        }
        .custom-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: var(--radius-full);
          outline: none;
          cursor: pointer;
          transition: all var(--transition-fast);
        }
        /* Webkit Thumb */
        .custom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: var(--radius-full);
          background: var(--text-primary);
          border: 2px solid var(--primary);
          box-shadow: var(--shadow-sm);
          transition: all var(--transition-fast);
        }
        .custom-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 0 4px var(--primary-light);
        }
        .custom-slider::-webkit-slider-thumb:active {
          transform: scale(1.1);
          background: var(--primary);
          border-color: var(--text-primary);
        }
        /* Firefox Thumb */
        .custom-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: var(--radius-full);
          background: var(--text-primary);
          border: 2px solid var(--primary);
          box-shadow: var(--shadow-sm);
          transition: all var(--transition-fast);
        }
        .custom-slider::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 0 4px var(--primary-light);
        }
        .custom-slider::-moz-range-thumb:active {
          transform: scale(1.1);
          background: var(--primary);
          border-color: var(--text-primary);
        }
        .slider-footer {
          display: flex;
          justify-content: space-between;
          margin-top: var(--space-1);
        }
        .slider-limit {
          font-size: var(--text-2xs);
          color: var(--text-muted);
          font-weight: var(--fw-bold);
        }
      `}</style>
    </div>
  );
}
